import { UserPlus } from "lucide-react";
import { Card, CardContent } from "../../../client/components/ui/card";
import useTranslation from "../../../client/hooks/useTranslation";
import {
  DIRECT_SOURCE,
  type SourceRow,
} from "../../../analytics/adminInsights";

interface Props {
  signupsLast7Days: number;
  signupsLast30Days: number;
  sources: SourceRow[];
}

const AcquisitionCard = ({
  signupsLast7Days,
  signupsLast30Days,
  sources,
}: Props) => {
  const t = useTranslation();
  const totalSourced = sources.reduce((sum, row) => sum + row.count, 0);

  return (
    <div>
      <h2 className="text-foreground mb-1 text-lg font-semibold">
        {t.adminDashboard.acquisitionTitle}
      </h2>
      <p className="text-muted-foreground mb-4 text-xs">
        {t.adminDashboard.acquisitionHint}
      </p>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-4">
          {[
            { label: t.adminDashboard.signups7d, value: signupsLast7Days },
            { label: t.adminDashboard.signups30d, value: signupsLast30Days },
          ].map(({ label, value }) => (
            <Card key={label}>
              <CardContent className="flex items-center gap-3 pt-4">
                <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-full">
                  <UserPlus className="text-primary size-4" />
                </div>
                <div>
                  <p className="text-foreground font-mono text-xl font-bold">
                    {value}
                  </p>
                  <p className="text-muted-foreground text-xs font-medium">
                    {label}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="lg:col-span-2">
          <CardContent className="pt-4">
            {sources.length === 0 ? (
              <p className="text-muted-foreground py-6 text-center text-xs">
                {t.adminDashboard.sourceEmpty}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-border border-b text-left text-xs">
                    <th className="pb-2 font-medium">
                      {t.adminDashboard.sourceLabel}
                    </th>
                    <th className="pb-2 text-right font-medium">
                      {t.adminDashboard.signups30d}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((row) => {
                    const share =
                      totalSourced === 0
                        ? 0
                        : Math.round((row.count / totalSourced) * 100);
                    return (
                      <tr
                        key={row.source}
                        className="border-border/60 border-b last:border-b-0"
                      >
                        <td className="text-foreground py-2.5">
                          {row.source === DIRECT_SOURCE
                            ? t.adminDashboard.sourceDirect
                            : row.source}
                        </td>
                        <td className="py-2.5 text-right">
                          <span className="text-foreground font-mono">
                            {row.count}
                          </span>
                          <span className="text-muted-foreground ml-2 font-mono text-xs">
                            {share}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AcquisitionCard;
